/**
 * Sales Funnel Page - Admin 4-stage kanban board
 * Stages: Intel -> Assess -> Analyze -> Strategize
 */
import React, { useState, useEffect } from 'react';
import {
  Loader2,
  Plus,
  Search,
  Users,
  Target,
  BarChart3,
  Lightbulb,
  ChevronRight,
  MoreVertical,
  Edit2,
  Trash2,
  X,
  Building2,
  Mail,
  Phone,
  Globe,
  Tag,
  Clock,
  ArrowRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import dataService from '../services/data';
import { FunnelKanbanData, FunnelLeadSummary, FunnelLead, FunnelStage, FunnelLeadCreate, FunnelStats } from '../types';

const STAGE_CONFIG: Record<FunnelStage, {
  title: string;
  subtitle: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  borderColor: string;
  aiAssistance: string;
  outputs: string[];
}> = {
  intel: {
    title: 'Intel',
    subtitle: 'Intelligence Gathering',
    icon: Search,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    aiAssistance: 'Call/email/text analysis, background research, company profiling',
    outputs: ['Business Profile', 'Key Contacts', 'Communication Log']
  },
  assess: {
    title: 'Assess',
    subtitle: 'Opportunity Assessment',
    icon: Target,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    aiAssistance: 'Opportunity scoring, talent identification, IP/technology assessment',
    outputs: ['Opportunity Score', 'Talent Matrix', 'IP Inventory']
  },
  analyze: {
    title: 'Analyze',
    subtitle: 'Platform Assessment',
    icon: BarChart3,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    aiAssistance: 'Gap analysis, fit scoring, resource matching',
    outputs: ['Assessment Recommendation', 'Gap Report']
  },
  strategize: {
    title: 'Strategize',
    subtitle: 'Strategy Development',
    icon: Lightbulb,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    aiAssistance: 'Resource allocation, partner matching, timeline development',
    outputs: ['Engagement Strategy', 'Resource Plan', 'Partner Recommendations']
  }
};

const STAGES: FunnelStage[] = ['intel', 'assess', 'analyze', 'strategize'];

const SalesFunnelPage: React.FC = () => {
  const [kanbanData, setKanbanData] = useState<FunnelKanbanData | null>(null);
  const [stats, setStats] = useState<FunnelStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<FunnelLead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New lead form state
  const [newLead, setNewLead] = useState<FunnelLeadCreate>({
    lead_name: '',
    company_name: '',
    contact_email: '',
    website: '',
    notes: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [kanbanResult, statsResult] = await Promise.all([
        dataService.fetchFunnelKanban(),
        dataService.fetchFunnelStats()
      ]);

      if (kanbanResult.error) {
        toast.error(kanbanResult.error);
      } else {
        setKanbanData(kanbanResult.data || null);
      }

      if (statsResult.data) {
        setStats(statsResult.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateLead = async () => {
    if (!newLead.lead_name || !newLead.company_name) {
      toast.error('Lead name and company name are required');
      return;
    }

    setIsSubmitting(true);
    const result = await dataService.createFunnelLead(newLead);
    setIsSubmitting(false);

    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Lead created successfully');
      setShowAddModal(false);
      setNewLead({
        lead_name: '',
        company_name: '',
        contact_email: '',
        website: '',
        notes: '',
        tags: []
      });
      loadData();
    }
  };

  const handleViewLead = async (leadId: number) => {
    const result = await dataService.fetchFunnelLead(leadId);
    if (result.error) {
      toast.error(result.error);
    } else if (result.data) {
      setSelectedLead(result.data);
      setShowDetailModal(true);
    }
  };

  const handleTransitionStage = async (leadId: number, toStage: FunnelStage) => {
    const result = await dataService.transitionLeadStage(leadId, { to_stage: toStage });
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success(`Lead moved to ${STAGE_CONFIG[toStage].title}`);
      loadData();
      if (selectedLead && selectedLead.id === leadId) {
        setSelectedLead(result.data || null);
      }
    }
  };

  const handleDeleteLead = async (leadId: number) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;

    const result = await dataService.deleteFunnelLead(leadId);
    if (result.error) {
      toast.error(result.error);
    } else {
      toast.success('Lead deleted');
      setShowDetailModal(false);
      setSelectedLead(null);
      loadData();
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newLead.tags?.includes(tagInput.trim())) {
      setNewLead({
        ...newLead,
        tags: [...(newLead.tags || []), tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setNewLead({
      ...newLead,
      tags: newLead.tags?.filter(t => t !== tag) || []
    });
  };

  const filteredKanbanData = kanbanData ? {
    intel: kanbanData.intel.filter(lead =>
      lead.lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    assess: kanbanData.assess.filter(lead =>
      lead.lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    analyze: kanbanData.analyze.filter(lead =>
      lead.lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    strategize: kanbanData.strategize.filter(lead =>
      lead.lead_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  } : null;

  const getNextStage = (currentStage: FunnelStage): FunnelStage | null => {
    const idx = STAGES.indexOf(currentStage);
    return idx < STAGES.length - 1 ? STAGES[idx + 1] : null;
  };

  const getPrevStage = (currentStage: FunnelStage): FunnelStage | null => {
    const idx = STAGES.indexOf(currentStage);
    return idx > 0 ? STAGES[idx - 1] : null;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales Funnel</h1>
            <p className="text-sm text-gray-600 mt-1">
              Track business touchpoints through Intel, Assess, Analyze, and Strategize stages
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Lead
          </button>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
            </div>
            {STAGES.map(stage => (
              <div key={stage} className={`rounded-lg border p-4 ${STAGE_CONFIG[stage].bgColor} ${STAGE_CONFIG[stage].borderColor}`}>
                <p className={`text-sm ${STAGE_CONFIG[stage].color}`}>{STAGE_CONFIG[stage].title}</p>
                <p className={`text-2xl font-bold ${STAGE_CONFIG[stage].color}`}>
                  {stats.stage_counts[stage] || 0}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Kanban Board */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading funnel...</span>
          </div>
        ) : filteredKanbanData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {STAGES.map((stage) => {
              const config = STAGE_CONFIG[stage];
              const Icon = config.icon;
              const leads = filteredKanbanData[stage];

              return (
                <div key={stage} className={`rounded-xl border-2 ${config.borderColor} ${config.bgColor} overflow-hidden`}>
                  {/* Column Header */}
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${config.bgColor}`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-semibold ${config.color}`}>{config.title}</h3>
                        <p className="text-xs text-gray-500">{config.subtitle}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
                        {leads.length}
                      </span>
                    </div>
                    {/* AI Assistance info */}
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">AI:</span> {config.aiAssistance}
                    </div>
                  </div>

                  {/* Lead Cards */}
                  <div className="p-3 space-y-3 min-h-[200px] max-h-[500px] overflow-y-auto">
                    {leads.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No leads in {config.title}</p>
                      </div>
                    ) : (
                      leads.map((lead) => (
                        <div
                          key={lead.id}
                          onClick={() => handleViewLead(lead.id)}
                          className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 truncate">{lead.lead_name}</h4>
                              <p className="text-sm text-gray-500 truncate">{lead.company_name}</p>
                            </div>
                            {lead.opportunity_score && (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                lead.opportunity_score >= 70 ? 'bg-green-100 text-green-700' :
                                lead.opportunity_score >= 40 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {lead.opportunity_score}
                              </span>
                            )}
                          </div>

                          {lead.tags && lead.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {lead.tags.slice(0, 2).map(tag => (
                                <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                  {tag}
                                </span>
                              ))}
                              {lead.tags.length > 2 && (
                                <span className="px-1.5 py-0.5 text-gray-400 text-xs">
                                  +{lead.tags.length - 2}
                                </span>
                              )}
                            </div>
                          )}

                          {lead.updated_at && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                              <Clock className="w-3 h-3" />
                              {new Date(lead.updated_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  {/* Stage Outputs */}
                  <div className="p-3 bg-white border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Outputs:</p>
                    <div className="flex flex-wrap gap-1">
                      {config.outputs.map(output => (
                        <span key={output} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {output}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Failed to load funnel data
          </div>
        )}

        {/* Add Lead Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Add New Lead</h2>
                  <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lead Name *</label>
                  <input
                    type="text"
                    value={newLead.lead_name}
                    onChange={(e) => setNewLead({ ...newLead, lead_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Q4 Partnership Opportunity"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={newLead.company_name}
                    onChange={(e) => setNewLead({ ...newLead, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Acme Corp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={newLead.contact_email || ''}
                    onChange={(e) => setNewLead({ ...newLead, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                  <input
                    type="url"
                    value={newLead.website || ''}
                    onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add tag and press Enter"
                    />
                    <button onClick={addTag} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  {newLead.tags && newLead.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newLead.tags.map(tag => (
                        <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-sm rounded">
                          {tag}
                          <button onClick={() => removeTag(tag)} className="hover:text-blue-900">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newLead.notes || ''}
                    onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Initial notes about this lead..."
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLead}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Create Lead'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lead Detail Modal */}
        {showDetailModal && selectedLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className={`p-6 border-b ${STAGE_CONFIG[selectedLead.current_stage].bgColor} ${STAGE_CONFIG[selectedLead.current_stage].borderColor}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${STAGE_CONFIG[selectedLead.current_stage].bgColor} ${STAGE_CONFIG[selectedLead.current_stage].color}`}>
                        {STAGE_CONFIG[selectedLead.current_stage].title}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold mt-2">{selectedLead.lead_name}</h2>
                    <p className="text-gray-600">{selectedLead.company_name}</p>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedLead.contact_email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${selectedLead.contact_email}`} className="hover:text-blue-600">
                        {selectedLead.contact_email}
                      </a>
                    </div>
                  )}
                  {selectedLead.contact_phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      {selectedLead.contact_phone}
                    </div>
                  )}
                  {selectedLead.website && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Globe className="w-4 h-4" />
                      <a href={selectedLead.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600">
                        {selectedLead.website}
                      </a>
                    </div>
                  )}
                </div>

                {/* Scores */}
                {(selectedLead.opportunity_score || selectedLead.talent_score || selectedLead.ip_score) && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Assessment Scores</h3>
                    <div className="grid grid-cols-3 gap-4">
                      {selectedLead.opportunity_score !== undefined && (
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600">Opportunity</p>
                          <p className="text-2xl font-bold text-blue-700">{selectedLead.opportunity_score}</p>
                        </div>
                      )}
                      {selectedLead.talent_score !== undefined && (
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-amber-600">Talent</p>
                          <p className="text-2xl font-bold text-amber-700">{selectedLead.talent_score}</p>
                        </div>
                      )}
                      {selectedLead.ip_score !== undefined && (
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600">IP</p>
                          <p className="text-2xl font-bold text-purple-700">{selectedLead.ip_score}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedLead.tags && selectedLead.tags.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedLead.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedLead.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
                    <p className="text-gray-600 text-sm whitespace-pre-wrap">{selectedLead.notes}</p>
                  </div>
                )}

                {/* Resource Matching Table (Stage 4) */}
                {selectedLead.current_stage === 'strategize' && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Resource Matching</h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Resource Type</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Match</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr>
                            <td className="px-4 py-2 font-medium">Our Resources</td>
                            <td className="px-4 py-2">
                              {selectedLead.our_resources_match?.length || 0} matched
                            </td>
                            <td className="px-4 py-2 text-gray-500">
                              AI platforms, technical capabilities, analysis tools
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 font-medium">Our Partners</td>
                            <td className="px-4 py-2">
                              {selectedLead.our_partners_match?.length || 0} matched
                            </td>
                            <td className="px-4 py-2 text-gray-500">
                              Strategic alliances, service providers, co-investors
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 font-medium">Our Talent</td>
                            <td className="px-4 py-2">
                              {selectedLead.our_talent_match?.length || 0} matched
                            </td>
                            <td className="px-4 py-2 text-gray-500">
                              BAT-A-T network, advisors, executives-in-residence
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2 font-medium">Our IP</td>
                            <td className="px-4 py-2">
                              {selectedLead.our_ip_match?.length || 0} matched
                            </td>
                            <td className="px-4 py-2 text-gray-500">
                              Proprietary methodologies, frameworks, technology
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Stage Transition Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex gap-2">
                    {getPrevStage(selectedLead.current_stage) && (
                      <button
                        onClick={() => handleTransitionStage(selectedLead.id, getPrevStage(selectedLead.current_stage)!)}
                        className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Move to {STAGE_CONFIG[getPrevStage(selectedLead.current_stage)!].title}
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDeleteLead(selectedLead.id)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {getNextStage(selectedLead.current_stage) && (
                      <button
                        onClick={() => handleTransitionStage(selectedLead.id, getNextStage(selectedLead.current_stage)!)}
                        className={`px-4 py-2 rounded-lg text-sm text-white flex items-center gap-2 ${
                          selectedLead.current_stage === 'intel' ? 'bg-amber-600 hover:bg-amber-700' :
                          selectedLead.current_stage === 'assess' ? 'bg-purple-600 hover:bg-purple-700' :
                          'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        Advance to {STAGE_CONFIG[getNextStage(selectedLead.current_stage)!].title}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SalesFunnelPage;
